import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop component that scrolls to top on forward navigation
 * but preserves scroll position on back navigation.
 */
export default function ScrollToTop() {
  const location = useLocation();
  const isInitialMount = useRef(true);
  const scrollPositions = useRef<Map<string, number>>(new Map());
  const isNavigatingBack = useRef(false);
  const navigationStack = useRef<string[]>([]);

  // Track popstate events globally (runs before React Router processes them)
  useEffect(() => {
    const handlePopState = () => {
      isNavigatingBack.current = true;
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    const currentPath = location.pathname + location.search;

    // On initial mount, don't scroll
    if (isInitialMount.current) {
      isInitialMount.current = false;
      navigationStack.current = [currentPath];
      return;
    }

    // Check if we're navigating to a path we've seen before (back navigation)
    const wasBackNavigation = isNavigatingBack.current || 
                              navigationStack.current.includes(currentPath);

    // If navigating back, restore scroll position
    if (wasBackNavigation) {
      const savedPosition = scrollPositions.current.get(currentPath);
      if (savedPosition !== undefined && savedPosition > 0) {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          window.scrollTo({ top: savedPosition, behavior: 'instant' });
        });
      } else {
        // If no saved position, scroll to top
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
      
      // Update navigation stack - remove paths after this one
      const currentIndex = navigationStack.current.indexOf(currentPath);
      if (currentIndex !== -1) {
        navigationStack.current = navigationStack.current.slice(0, currentIndex + 1);
      }
      
      isNavigatingBack.current = false;
      return;
    }

    // Forward navigation - scroll to top and add to stack
    window.scrollTo({ top: 0, behavior: 'instant' });
    navigationStack.current.push(currentPath);
    
    // Limit stack size to prevent memory issues
    if (navigationStack.current.length > 50) {
      navigationStack.current = navigationStack.current.slice(-50);
    }
  }, [location]);

  // Save scroll position continuously
  useEffect(() => {
    const handleScroll = () => {
      const currentPath = location.pathname + location.search;
      scrollPositions.current.set(currentPath, window.scrollY);
    };

    // Throttle scroll events to avoid performance issues
    let scrollTimeout: ReturnType<typeof setTimeout>;
    const throttledScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(handleScroll, 100);
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });

    // Save current position immediately
    handleScroll();

    return () => {
      window.removeEventListener('scroll', throttledScroll);
      clearTimeout(scrollTimeout);
    };
  }, [location]);

  return null;
}

